import { AnimatePresence, motion } from "motion/react"
import type { AppNotification, NotificationType } from "@/types/notification"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { HugeiconsIcon, Icons } from "@/components/icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

import { Button } from "@/components/ui/button"
import { EmptyState } from "../ui/empty-state"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TimeAgo } from "@/components/ui/time-ago"
import { cn } from "@/lib/utils"
import { normalizeNotificationType } from "@/types/notification"
import { useBreakpoint } from "@/hooks/use-breakpoint"
import { useDelayLoading } from "@/hooks/use-delay-loading"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"
import { useNotificationContext } from "@/contexts/notification-context"
import {
  getNotificationCtaLabelKey,
  getNotificationDisplayType,
  getNotificationHref,
} from "@/lib/notifications/notification-behaviors"

const typeStyles: Record<
  NotificationType,
  {
    barBg: string
    icon: (typeof Icons)[keyof typeof Icons]
    iconClass: string
    bg: string
  }
> = {
  warning: {
    barBg: "bg-warning",
    icon: Icons.Alert01,
    iconClass: "text-warning",
    bg: "bg-warning-5",
  },
  success: {
    barBg: "bg-success",
    icon: Icons.CheckmarkCircle01,
    iconClass: "text-success",
    bg: "bg-success-5",
  },
  error: {
    barBg: "bg-error",
    icon: Icons.AlertCircle,
    iconClass: "text-error",
    bg: "bg-error-5",
  },
  info: {
    barBg: "bg-info",
    icon: Icons.InformationCircle,
    iconClass: "text-info",
    bg: "bg-info-5",
  },
}

function NotificationItemRow({
  item,
  onSelect,
  href,
  ctaLabel,
  isDesktop,
}: {
  item: AppNotification
  onSelect: (id: string, href?: string) => void
  href?: string
  ctaLabel?: string
  isDesktop: boolean
}) {
  const displayType = normalizeNotificationType(
    getNotificationDisplayType(item) ?? item.type
  )
  const style = typeStyles[displayType]

  return (
    <div
      onClick={() => {
        if (!isDesktop) onSelect(item.id, href)
      }}
      className={cn(
        "group relative flex w-full items-start gap-3 overflow-hidden rounded-xl p-3 pr-4 text-left transition-all duration-200",
        style.bg,
        href &&
          !isDesktop &&
          "cursor-pointer hover:-translate-y-0.5 hover:brightness-95 hover:shadow-sm"
      )}
    >
      <AnimatePresence>
        {!item.read && (
          <motion.div
            initial={{ width: 4 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute top-0 left-0 h-full w-1 shrink-0 self-stretch overflow-hidden rounded-l",
              style.barBg
            )}
            aria-hidden
          />
        )}
      </AnimatePresence>
      <HugeiconsIcon
        icon={style.icon}
        size={16}
        className={cn("shrink-0", style.iconClass)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-start gap-2 pr-24">
          <p className="text-primary-text app-text-body-small min-w-0 flex-1 truncate">
            {item.title}
          </p>
        </div>
        <div className="absolute top-3 right-3 transition-opacity duration-200 xl:group-hover:opacity-0">
          <TimeAgo date={item.createdAt} />
        </div>
        {href && ctaLabel ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onSelect(item.id, href)
            }}
            className="pointer-events-auto absolute top-3 right-3 hidden translate-x-1 cursor-pointer items-center gap-1 text-xs leading-none font-semibold text-primary opacity-0 transition-all duration-200 xl:flex xl:group-hover:translate-x-0 xl:group-hover:opacity-100"
          >
            <span>{ctaLabel}</span>
            <HugeiconsIcon
              icon={Icons.ArrowRight02}
              size={12}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </button>
        ) : null}
        {item.description ? (
          <p className="text-secondary-text app-text-body-small">
            {item.description}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function NotificationListContent({
  onItemSelect,
  className,
}: {
  onItemSelect: (id: string, href?: string) => void
  className?: string
}) {
  const intl = useIntl()
  const { isDesktop } = useBreakpoint()
  const {
    notifications,
    refetchNotifications,
    isRefetchingNotifications,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = useNotificationContext()

  const showReloadLoading = useDelayLoading(isRefetchingNotifications, 500)

  const { pageEndRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    isLoading: isFetchingNextPage,
  })

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex h-[min(400px,70vh)] items-center justify-center px-2",
          className
        )}
      >
        <div className="text-muted-foreground flex items-center gap-2">
          <HugeiconsIcon
            icon={Icons.Loading01}
            size={16}
            className="animate-spin"
          />
          <span className="text-sm">
            {intl.formatMessage({ id: "common.loading" })}
          </span>
        </div>
      </div>
    )
  }

  const isEmpty = notifications.length === 0
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className={cn("flex w-full justify-end px-2", isEmpty && "hidden")}>
        <Button
          variant="ghost"
          onClick={refetchNotifications}
          disabled={showReloadLoading}
          className="h-5 self-end p-0 hover:bg-transparent disabled:opacity-60"
          startContent={
            <HugeiconsIcon
              icon={Icons.Refresh01}
              size={18}
              className={cn(showReloadLoading && "animate-spin")}
            />
          }
        >
          {intl.formatMessage({ id: "common.reload", defaultMessage: "Reload" })}
        </Button>
      </div>
      {isEmpty ? (
        <div className="flex h-[min(400px,70vh)] items-center justify-center px-2">
          <EmptyState
            title={intl.formatMessage({ id: "layout.noNotifications" })}
          />
        </div>
      ) : (
        <ScrollArea className="h-[min(400px,70vh)] px-2">
          <div className="flex flex-col gap-2">
            {notifications.map((item) => (
              <NotificationItemRow
                key={item.id}
                item={item}
                onSelect={onItemSelect}
                href={getNotificationHref(item)}
                ctaLabel={(() => {
                  const ctaLabelKey = getNotificationCtaLabelKey(item)
                  return ctaLabelKey
                    ? intl.formatMessage({ id: ctaLabelKey })
                    : undefined
                })()}
                isDesktop={isDesktop}
              />
            ))}
            {hasNextPage && (
              <div
                ref={pageEndRef}
                className="flex items-center justify-center py-2"
              >
                {isFetchingNextPage && (
                  <HugeiconsIcon
                    icon={Icons.Loading01}
                    size={16}
                    className="text-secondary-text animate-spin"
                  />
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}

export function NotificationListPanel({
  className,
  triggerClassName,
  onItemSelect,
}: {
  className?: string
  triggerClassName?: string
  onItemSelect?: (id: string) => void
}) {
  const intl = useIntl()
  const { isTablet } = useBreakpoint()
  const { unreadCount, markRead } = useNotificationContext()
  const navigate = useNavigate()

  const handleItemSelect = (id: string, href?: string) => {
    markRead(id)
    onItemSelect?.(id)
    if (href) navigate(href)
  }

  const listContent = (
    <NotificationListContent onItemSelect={handleItemSelect} />
  )

  const triggerProps = {
    triggerClassName,
    unreadCount,
    ariaLabel: intl.formatMessage({ id: "layout.notifications" }),
  }

  const triggerButton = (
    <button
      type="button"
      className={cn(
        "bg-secondary-background relative flex h-10 items-center justify-center gap-2 overflow-visible rounded-xl border-none px-2.5 py-2",
        triggerProps.triggerClassName
      )}
      aria-label={triggerProps.ariaLabel}
    >
      <HugeiconsIcon
        icon={Icons.Notification01}
        size={20}
        className="text-primary-text"
      />
      {triggerProps.unreadCount > 0 && (
        <span className="bg-primary text-background app-text-label-small absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full px-1">
          {triggerProps.unreadCount > 99 ? "99+" : triggerProps.unreadCount}
        </span>
      )}
    </button>
  )

  if (!isTablet) {
    return (
      <Drawer>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent
          className={cn(
            "shadow-1-light flex max-h-[85vh] flex-col border-t",
            className
          )}
        >
          <DrawerHeader className="hidden shrink-0 items-center gap-2 border-b px-4 pt-2 pb-3 text-left">
            <DrawerTitle className="text-primary-text app-text-title-large">
              {intl.formatMessage({ id: "layout.notifications" })}
            </DrawerTitle>
          </DrawerHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-0">
            {listContent}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className={cn(
          "shadow-1-light bg-background flex w-107 flex-col gap-2 rounded-2xl px-0 py-2",
          className
        )}
      >
        {listContent}
      </PopoverContent>
    </Popover>
  )
}
